import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";

// --- Component Imports ---
import Sidebar from "./Sidebar";
import AccountSetup from "./steps/AccountSetup";
import EducationalBackground from "./steps/EducationalBackground";
import ProfessionalExperience from "./steps/ProfessionalExperience";
import SkillsExpertise from "./steps/SkillsExpertise";
import LoadingOverlay from "./common/LoadingOverlay";

// --- Styles ---
import "../App.css";

// --- Asset Imports ---
import ProfileIcon from "../assets/ProfileSetup/SetupYourProfile.svg";
import EducationIcon from "../assets/ProfileSetup/EducationalBackground.svg";
import ExperienceIcon from "../assets/ProfileSetup/ProfessionalExperience.svg";
import SkillsIcon from "../assets/ProfileSetup/SkillAndExpertise.svg";

// --- API & Helper Imports ---
import {
  getUserProfile as fetchBackendUserProfileAPI,
  updateUserProfile,
  uploadProfilePicture,
  getAllEducation,
  addEducationRecord,
  updateEducationRecord,
  deleteEducationRecord,
  getOrCreateInstitution,
  getAllExperience,
  addExperience,
  updateExperience,
  deleteExperience,
  getUserSkills,
  getOrCreateSkill,
  addUserSkill,
  deleteUserSkill,
  getUserCertifications,
  getOrCreateCertificateDefinition,
  addUserCertification,
  deleteUserCertification,
  uploadAndParseResume,
} from "../services/api";
import { createProfilePictureFormData } from "../services/apiHelpers";

// --- Context Imports ---
import { useAuth } from "../contexts/AuthContext";
import { useProfile, ProfileProvider, STEPS } from "../contexts/ProfileContext";

// --- Component Configuration ---
const stepsConfig = [
  {
    id: "account",
    label: "Account Setup",
    section: "account",
    icon: ProfileIcon,
    stepIndex: STEPS.ACCOUNT,
  },
  {
    id: "education",
    label: "Educational Background",
    section: "educations",
    icon: EducationIcon,
    stepIndex: STEPS.EDUCATION,
  },
  {
    id: "experience",
    label: "Professional Experience",
    section: "experiences",
    icon: ExperienceIcon,
    stepIndex: STEPS.EXPERIENCE,
  },
  {
    id: "skills",
    label: "Skills & Expertise",
    section: "skills",
    icon: SkillsIcon,
    stepIndex: STEPS.SKILLS,
  },
];

function ProfileSetupContent() {
  const navigate = useNavigate();
  const {
    currentUser: authContextUser,
    fetchUserProfile: refreshAuthContextProfile,
    logout,
  } = useAuth();
  const {
    formData,
    currentStep,
    isSubmitting,
    error: contextErrorFromProfile,
    initializeForm,
    setStep,
    setError: setContextErrorFromProfile,
    setSubmitting,
    goToNextStep,
    goToPreviousStep,
    clearAllData, // Add this from context
  } = useProfile();

  const [loadingInitialData, setLoadingInitialData] = useState(true);
  const [isParsingAI, setIsParsingAI] = useState(false);
  const hasFetchedData = useRef(false);
  const currentUserIdRef = useRef(null);

  const splitFullName = (fullName) => {
    if (!fullName || typeof fullName !== "string")
      return { firstName: "", lastName: "" };
    const names = fullName.trim().split(/\s+/);
    return { firstName: names.shift() || "", lastName: names.join(" ") || "" };
  };

  const refreshProfileData = useCallback(async () => {
    const userId = currentUserIdRef.current;
    if (!userId) return;

    setLoadingInitialData(true);
    setContextErrorFromProfile(null);

    try {
      const [
        profileDataFromBackend,
        fetchedEducations,
        fetchedExperiences,
        fetchedUserSkills,
        fetchedUserCertifications,
      ] = await Promise.all([
        fetchBackendUserProfileAPI(),
        getAllEducation().catch(() => []),
        getAllExperience().catch(() => []),
        getUserSkills().catch(() => []),
        getUserCertifications().catch(() => []),
      ]);

      // Combine all skills and tools into a single skills array
      const processedSkillsExpertise = { skills: [], licenses: [] };
      if (Array.isArray(fetchedUserSkills)) {
        fetchedUserSkills.forEach((us) => {
          if (us?.skill?.name) {
            // Add all skills regardless of category to the skills array
            processedSkillsExpertise.skills.push(us.skill.name);
          }
        });
      }
      if (Array.isArray(fetchedUserCertifications)) {
        fetchedUserCertifications.forEach((uc) => {
          if (uc?.definition?.name) {
            processedSkillsExpertise.licenses.push(uc.definition.name);
          }
        });
      }
      // Remove duplicates
      processedSkillsExpertise.skills = [
        ...new Set(processedSkillsExpertise.skills),
      ];
      processedSkillsExpertise.licenses = [
        ...new Set(processedSkillsExpertise.licenses),
      ];

      const { firstName, lastName } = splitFullName(
        profileDataFromBackend?.full_name
      );
      const newInitializedFormData = {
        userId: userId,
        account: {
          firstName: firstName || "",
          lastName: lastName || "",
          email: authContextUser?.email || profileDataFromBackend?.email || "",
          phoneNumber: profileDataFromBackend?.phone_number || "",
          accountType: profileDataFromBackend?.account_type || "Job Seeker",
          timeZone: profileDataFromBackend?.time_zone || "UTC",
          photoUrl: profileDataFromBackend?.profile_picture || null,
          photo: null,
          profession: profileDataFromBackend?.profession || "",
          headline: profileDataFromBackend?.headline || "",
          bio: profileDataFromBackend?.bio || "",
          location: profileDataFromBackend?.location || "",
        },
        educations: fetchedEducations || [],
        experiences: fetchedExperiences || [],
        skills: processedSkillsExpertise,
      };
      initializeForm(newInitializedFormData);
    } catch (error) {
      if (error.status === 404) {
        const emptyFormDataForNewUser = {
          userId: userId,
          account: {
            firstName: "",
            lastName: "",
            email: authContextUser?.email || "",
          },
          educations: [],
          experiences: [],
          skills: { skills: [], licenses: [] },
        };
        initializeForm(emptyFormDataForNewUser);
      } else {
        setContextErrorFromProfile(
          `Failed to load profile: ${error?.data?.detail || error.message}`
        );
      }
    } finally {
      setLoadingInitialData(false);
    }
  }, [authContextUser?.email, initializeForm, setContextErrorFromProfile]);

  useEffect(() => {
    const userIdFromAuth = authContextUser?.id;
    if (!userIdFromAuth) {
      currentUserIdRef.current = null;
      hasFetchedData.current = false;
      initializeForm({});
      setLoadingInitialData(false);
      return;
    }
    if (userIdFromAuth !== currentUserIdRef.current) {
      currentUserIdRef.current = userIdFromAuth;
      hasFetchedData.current = false;
    }
    if (!hasFetchedData.current) {
      hasFetchedData.current = true;
      refreshProfileData();
    }
  }, [authContextUser?.id, refreshProfileData, initializeForm]);

  // Add handler for clearing all data
  const handleClearAllData = useCallback(async () => {
    try {
      setSubmitting(true);
      setContextErrorFromProfile(null);

      // Clear backend data if user has saved information
      if (authContextUser?.id) {
        try {
          // Clear all education records
          const educations = await getAllEducation();
          for (const edu of educations) {
            await deleteEducationRecord(edu.id);
          }

          // Clear all experience records
          const experiences = await getAllExperience();
          for (const exp of experiences) {
            await deleteExperience(exp.id);
          }

          // Clear all skills
          const skills = await getUserSkills();
          for (const skill of skills) {
            await deleteUserSkill(skill.id);
          }

          // Clear all certifications
          const certs = await getUserCertifications();
          for (const cert of certs) {
            await deleteUserCertification(cert.id);
          }

          // Reset profile to defaults
          await updateUserProfile({
            first_name: "",
            last_name: "",
            phone_number: "",
            headline: "",
            bio: "",
            location: "",
            profile_picture: null,
          });
        } catch (backendError) {
          console.warn(
            "Some backend data may not have been cleared:",
            backendError
          );
          // Don't throw here - we still want to clear frontend data
        }
      }

      // Clear frontend form data (keep email from auth context)
      clearAllData({ keepEmail: true });

      // Show success message
      setTimeout(() => {
        alert("All profile data has been cleared successfully.");
      }, 100);
    } catch (error) {
      console.error("Error clearing data:", error);
      setContextErrorFromProfile("Failed to clear all data. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [
    authContextUser?.id,
    clearAllData,
    setSubmitting,
    setContextErrorFromProfile,
  ]);

  const handleSaveAccountAndNext = useCallback(
    async (accountDataFromStep) => {
      setSubmitting(true);
      setContextErrorFromProfile(null);
      let finalPhotoUrl = accountDataFromStep.photoUrl;
      try {
        if (accountDataFromStep.photo instanceof File) {
          const photoFormData = createProfilePictureFormData(
            accountDataFromStep.photo
          );
          const uploadResponse = await uploadProfilePicture(photoFormData);
          if (uploadResponse?.profile_picture)
            finalPhotoUrl = uploadResponse.profile_picture;
        }
        await updateUserProfile({
          first_name: accountDataFromStep.firstName,
          last_name: accountDataFromStep.lastName,
          phone_number: accountDataFromStep.phoneNumber,
          headline: accountDataFromStep.headline,
          bio: accountDataFromStep.bio,
          location: accountDataFromStep.location,
          profile_picture: finalPhotoUrl,
        });
        await refreshProfileData();
        goToNextStep();
      } catch (error) {
        setContextErrorFromProfile(
          `Failed to save account details: ${error.message}`
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      goToNextStep,
      refreshProfileData,
      setContextErrorFromProfile,
      setSubmitting,
    ]
  );

  const handleAddEducation = useCallback(
    async (eduFormData) => {
      setSubmitting(true);
      try {
        const institution = await getOrCreateInstitution({
          name: eduFormData.institutionName,
        });

        const payload = {
          institution_id: institution.id,
          degree: eduFormData.degree,
          field_of_study: eduFormData.fieldOfStudy,
          start_date: eduFormData.startDate,
          end_date: eduFormData.endDate || null,
          description: eduFormData.description || "",
        };

        await addEducationRecord(payload);
        await refreshProfileData();
        return true;
      } catch (err) {
        setContextErrorFromProfile(`Add Education Failed: ${err.message}`);
        console.error("API Validation Error (Add):", err.data);
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [refreshProfileData, setContextErrorFromProfile, setSubmitting]
  );

  const handleUpdateEducation = useCallback(
    async (index, eduFormData) => {
      const recordToUpdate = formData.educations?.[index];
      if (!recordToUpdate?.id) return false;
      setSubmitting(true);
      try {
        const institution = await getOrCreateInstitution({
          name: eduFormData.institutionName,
        });

        const payload = {
          institution_id: institution.id,
          degree: eduFormData.degree,
          field_of_study: eduFormData.fieldOfStudy,
          start_date: eduFormData.startDate,
          end_date: eduFormData.endDate || null,
          description: eduFormData.description || "",
        };

        await updateEducationRecord(recordToUpdate.id, payload);
        await refreshProfileData();
        return true;
      } catch (err) {
        setContextErrorFromProfile(`Update Education Failed: ${err.message}`);
        console.error("API Validation Error (Update):", err.data);
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [
      formData.educations,
      refreshProfileData,
      setContextErrorFromProfile,
      setSubmitting,
    ]
  );

  const handleRemoveEducation = useCallback(
    async (index) => {
      const recordToRemove = formData.educations?.[index];
      if (!recordToRemove?.id) return false;
      setSubmitting(true);
      try {
        await deleteEducationRecord(recordToRemove.id);
        await refreshProfileData();
        return true;
      } catch (err) {
        setContextErrorFromProfile(`Remove Education Failed: ${err.message}`);
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [
      formData.educations,
      refreshProfileData,
      setContextErrorFromProfile,
      setSubmitting,
    ]
  );

  const handleAddExperience = useCallback(
    async (expFormData) => {
      setSubmitting(true);
      try {
        const payload = {
          title: expFormData.jobTitle,
          company: expFormData.companyName,
          location: expFormData.location,
          start_date: expFormData.startDate,
          end_date: expFormData.isCurrent ? null : expFormData.endDate,
          is_current: expFormData.isCurrent || false,
          description: expFormData.description || "",
        };

        await addExperience(payload);
        await refreshProfileData();
        return true;
      } catch (err) {
        setContextErrorFromProfile(`Add Experience Failed: ${err.message}`);
        console.error("API Validation Error (Add Experience):", err.data);
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [refreshProfileData, setContextErrorFromProfile, setSubmitting]
  );

  const handleUpdateExperience = useCallback(
    async (index, expFormData) => {
      const recordToUpdate = formData.experiences?.[index];
      if (!recordToUpdate?.id) return false;
      setSubmitting(true);
      try {
        const payload = {
          title: expFormData.jobTitle,
          company: expFormData.companyName,
          location: expFormData.location,
          start_date: expFormData.startDate,
          end_date: expFormData.isCurrent ? null : expFormData.endDate,
          is_current: expFormData.isCurrent || false,
          description: expFormData.description || "",
        };

        await updateExperience(recordToUpdate.id, payload);
        await refreshProfileData();
        return true;
      } catch (err) {
        setContextErrorFromProfile(`Update Experience Failed: ${err.message}`);
        console.error("API Validation Error (Update Experience):", err.data);
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [
      formData.experiences,
      refreshProfileData,
      setContextErrorFromProfile,
      setSubmitting,
    ]
  );

  const handleRemoveExperience = useCallback(
    async (index) => {
      const recordToRemove = formData.experiences?.[index];
      if (!recordToRemove?.id) return false;
      setSubmitting(true);
      try {
        await deleteExperience(recordToRemove.id);
        await refreshProfileData();
        return true;
      } catch (err) {
        setContextErrorFromProfile(`Remove Experience Failed: ${err.message}`);
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [
      formData.experiences,
      refreshProfileData,
      setContextErrorFromProfile,
      setSubmitting,
    ]
  );

  const handleSaveSkillsAndComplete = useCallback(
    async (skillsDataFromStep) => {
      setSubmitting(true);
      setContextErrorFromProfile(null);

      try {
        const uiSkills = new Set(
          (skillsDataFromStep.skills || []).map((s) => s.trim()).filter(Boolean)
        );
        const uiLicenses = new Set(
          (skillsDataFromStep.licenses || [])
            .map((l) => l.trim())
            .filter(Boolean)
        );

        const [backendSkills, backendCerts] = await Promise.all([
          getUserSkills(),
          getUserCertifications(),
        ]);

        const backendSkillMap = new Map(
          backendSkills.map((s) => [
            s.skill.name,
            { id: s.id, category: s.skill.category },
          ])
        );
        const backendLicenseMap = new Map(
          backendCerts.map((c) => [c.definition.name, c.id])
        );

        // Add new skills (we'll categorize them as "skill" by default)
        for (const skillName of uiSkills) {
          if (!backendSkillMap.has(skillName)) {
            const skill = await getOrCreateSkill(skillName, "skill");
            await addUserSkill({ skill_id: skill.id });
          }
        }

        // Add new licenses
        for (const licenseName of uiLicenses) {
          if (!backendLicenseMap.has(licenseName)) {
            const certDef = await getOrCreateCertificateDefinition(licenseName);
            await addUserCertification({ certificate_id: certDef.id });
          }
        }

        // Delete skills that are no longer in the UI
        for (const [name, data] of backendSkillMap.entries()) {
          if (!uiSkills.has(name)) {
            console.log(
              `--- DELETING SKILL: '${name}' with user-skill ID: ${data.id} ---`
            );
            await deleteUserSkill(data.id);
          }
        }

        // Delete licenses that are no longer in the UI
        for (const [name, id] of backendLicenseMap.entries()) {
          if (!uiLicenses.has(name)) {
            console.log(
              `--- DELETING LICENSE: '${name}' with user-cert ID: ${id} ---`
            );
            await deleteUserCertification(id);
          }
        }

        if (refreshAuthContextProfile) {
          await refreshAuthContextProfile();
        }
        navigate("/dashboard");
      } catch (err) {
        const errorMessage = err.data?.detail || err.message;
        setContextErrorFromProfile(
          `Profile completion failed: ${errorMessage}`
        );
        console.error(
          "API Validation Error (Skills/Completion):",
          err.data || err
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      navigate,
      refreshAuthContextProfile,
      setContextErrorFromProfile,
      setSubmitting,
    ]
  );

  const handleAIAssistedFill = useCallback(
    async (resumeFile) => {
      if (!resumeFile) return;
      setIsParsingAI(true);
      setContextErrorFromProfile(null);
      try {
        await uploadAndParseResume(resumeFile);
        await refreshProfileData();
        if (refreshAuthContextProfile) {
          await refreshAuthContextProfile();
        }
        setTimeout(
          () =>
            alert(
              "Success! Your profile has been pre-filled. Please review the updated information."
            ),
          100
        );
      } catch (error) {
        setContextErrorFromProfile(
          `AI parsing failed: ${error?.data?.detail || error.message}`
        );
      } finally {
        setIsParsingAI(false);
      }
    },
    [refreshProfileData, refreshAuthContextProfile, setContextErrorFromProfile]
  );

  const handleSkipToDashboard = useCallback(async () => {
    try {
      if (refreshAuthContextProfile) {
        await refreshAuthContextProfile();
      }
    } catch (error) {
      console.error(
        "Failed to refresh profile before skipping, navigating anyway.",
        error
      );
    } finally {
      navigate("/dashboard");
    }
  }, [navigate, refreshAuthContextProfile]);

  const stepDataStatus = useMemo(() => {
    if (!formData) return [];
    return stepsConfig.map((sc) => {
      const data = formData[sc.section];
      switch (sc.section) {
        case "account":
          return !!(formData.account?.firstName && formData.account?.lastName);
        case "educations":
        case "experiences":
          return Array.isArray(data) && data.length > 0;
        case "skills":
          return !!(data?.skills?.length || data?.licenses?.length);
        default:
          return false;
      }
    });
  }, [formData]);

  const handleStepClick = useCallback(
    (stepIndex) => {
      if (stepIndex <= currentStep || stepDataStatus[stepIndex])
        setStep(stepIndex);
    },
    [currentStep, stepDataStatus, setStep]
  );

  const handleLogout = useCallback(async () => {
    await logout();
    navigate("/login");
  }, [logout, navigate]);

  const renderStep = () => {
    const config = stepsConfig.find((s) => s.stepIndex === currentStep);
    if (!config)
      return (
        <div className="p-6 text-red-600 font-semibold">
          Error: Invalid step.
        </div>
      );

    const commonProps = {
      onBack: currentStep > STEPS.ACCOUNT ? goToPreviousStep : null,
      isSubmitting,
      onSkipToDashboard: handleSkipToDashboard,
      error: contextErrorFromProfile,
      setError: setContextErrorFromProfile,
    };

    switch (config.id) {
      case "account":
        return (
          <AccountSetup
            {...commonProps}
            data={formData.account}
            onNext={handleSaveAccountAndNext}
            isParsingAI={isParsingAI}
            onAIAssistedFill={handleAIAssistedFill}
          />
        );
      case "education":
        return (
          <EducationalBackground
            {...commonProps}
            educations={formData.educations || []}
            onAddEducation={handleAddEducation}
            onUpdateEducation={handleUpdateEducation}
            onRemoveEducation={handleRemoveEducation}
            onNext={goToNextStep}
          />
        );
      case "experience":
        return (
          <ProfessionalExperience
            {...commonProps}
            experiences={formData.experiences || []}
            onAddExperience={handleAddExperience}
            onUpdateExperience={handleUpdateExperience}
            onRemoveExperience={handleRemoveExperience}
            onNext={goToNextStep}
          />
        );
      case "skills":
        return (
          <SkillsExpertise
            {...commonProps}
            data={formData.skills}
            onNext={handleSaveSkillsAndComplete}
          />
        );
      default:
        return <div>Component not found.</div>;
    }
  };

  return (
    <div className="profile-setup-container flex flex-col md:flex-row min-h-screen bg-gray-100">
      {isParsingAI && (
        <LoadingOverlay message="Analyzing resume and populating profile..." />
      )}
      {isSubmitting && currentStep === STEPS.SKILLS && (
        <LoadingOverlay message="Finalizing your profile..." />
      )}
      <div className="sidebar-container w-full md:w-72 lg:w-80 xl:w-96 bg-white shadow-lg border-r border-gray-200 md:h-screen md:sticky md:top-0 md:overflow-y-auto flex-shrink-0">
        <Sidebar
          currentStepIndex={currentStep}
          steps={stepsConfig}
          onStepClick={handleStepClick}
          stepDataStatus={stepDataStatus}
          onLogout={handleLogout}
          onClearData={handleClearAllData}
        />
      </div>
      <main className="main-content flex-1 p-4 sm:p-6 md:p-8 lg:p-10 overflow-y-auto">
        {contextErrorFromProfile && (
          <div
            className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6"
            role="alert"
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="font-bold">Error</p>
                <p>{contextErrorFromProfile}</p>
              </div>
              <button
                onClick={() => setContextErrorFromProfile(null)}
                className="ml-4 p-1 -m-1 text-red-600 hover:text-red-800 focus:outline-none rounded"
              >
                <svg
                  className="h-5 w-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </button>
            </div>
          </div>
        )}
        {loadingInitialData ? (
          <div className="flex justify-center items-center p-10 bg-white rounded-xl shadow-md min-h-[300px]">
            <svg
              className="animate-spin h-10 w-10 text-emerald-600 mr-3"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="text-gray-600 text-lg">Loading Profile Data...</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            {renderStep()}
          </div>
        )}
      </main>
    </div>
  );
}

function ProfileSetup() {
  return (
    <ProfileProvider>
      <ProfileSetupContent />
    </ProfileProvider>
  );
}

export default ProfileSetup;
